import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { 
  Plus, 
  UserMinus,
  Trash2,
  Play,
  Loader2,
  Calendar as CalendarIcon,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw,
  Users
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '@/context/AuthContext';

import { API_BASE_URL } from '@/lib/apiBase';

const API = API_BASE_URL;

const OffboardingPage = () => {
  const { isAdmin } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [locations, setLocations] = useState([]);
  const [crmUsers, setCrmUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [syncingUsers, setSyncingUsers] = useState(false);
  const [date, setDate] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [formData, setFormData] = useState({
    user_id: '',
    location_id: '',
    delete_calendar: true,
    revoke_access: true,
    notes: '',
    reason: ''
  });

  const fetchData = async () => {
    try {
      const [tasksRes, locationsRes] = await Promise.all([
        axios.get(`${API}/offboarding`),
        axios.get(`${API}/locations`)
      ]);
      setTasks(tasksRes.data);
      setLocations(locationsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCrmUsers = async (locationId) => {
    if (!locationId) {
      setCrmUsers([]);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/locations/${locationId}/crm-users`);
      setCrmUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch CRM users:', error);
      setCrmUsers([]);
    }
  };

  const syncCrmUsers = async () => {
    if (!selectedLocation) {
      toast.error('Please select a location first');
      return;
    }
    
    setSyncingUsers(true);
    try {
      const response = await axios.post(`${API}/locations/${selectedLocation}/sync-users`);
      toast.success(response.data.message);
      await fetchCrmUsers(selectedLocation);
    } catch (error) {
      toast.error('Failed to sync users');
    } finally {
      setSyncingUsers(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchCrmUsers(selectedLocation);
      setFormData(prev => ({ ...prev, location_id: selectedLocation, user_id: '' }));
    }
  }, [selectedLocation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.user_id) {
      toast.error('Please select a user to offboard');
      return;
    }
    if (!date) {
      toast.error('Please select an offboarding date');
      return;
    }
    if (!formData.reason) {
      toast.error('Please provide a reason for offboarding');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/offboarding`, {
        ...formData,
        offboarding_date: format(date, 'yyyy-MM-dd')
      });
      toast.success(isAdmin() ? 'Offboarding task scheduled' : 'Offboarding request submitted for approval');
      setDialogOpen(false);
      setFormData({ user_id: '', location_id: '', delete_calendar: true, revoke_access: true, notes: '', reason: '' });
      setDate(null);
      setSelectedLocation('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTask) return;
    
    try {
      await axios.delete(`${API}/offboarding/${selectedTask.id}`);
      toast.success('Task deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedTask(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const handleExecute = async () => {
    if (!selectedTask) return;
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/offboarding/${selectedTask.id}/execute`);
      toast.success('Offboarding executed successfully');
      setExecuteDialogOpen(false);
      setSelectedTask(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to execute offboarding');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (taskId) => {
    try {
      await axios.put(`${API}/offboarding/${taskId}/approve`);
      toast.success('Offboarding request approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    }
  };

  const handleReject = async (taskId) => {
    try {
      await axios.put(`${API}/offboarding/${taskId}/reject`);
      toast.success('Offboarding request rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending_approval: { icon: Clock, class: 'bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 text-xs font-medium', label: 'Pending Approval' },
      approved: { icon: CheckCircle, class: 'bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5 text-xs font-medium', label: 'Approved' },
      scheduled: { icon: Clock, class: 'bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-0.5 text-xs font-medium', label: 'Scheduled' },
      in_progress: { icon: Loader2, class: 'bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full px-2 py-0.5 text-xs font-medium', label: 'In Progress' },
      completed: { icon: CheckCircle, class: 'bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5 text-xs font-medium', label: 'Completed' },
      rejected: { icon: XCircle, class: 'bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5 text-xs font-medium', label: 'Rejected' },
      failed: { icon: XCircle, class: 'bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5 text-xs font-medium', label: 'Failed' },
    };
    const config = styles[status] || styles.scheduled;
    const Icon = config.icon;
    
    return (
      <span className={config.class}>
        <Icon className={`w-3 h-3 inline mr-1 ${status === 'in_progress' ? 'animate-spin' : ''}`} />
        {config.label}
      </span>
    );
  };

  const getSelectedUser = () => {
    return crmUsers.find(u => u.id === formData.user_id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="offboarding-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Offboarding
          </h1>
          <p className="text-slate-500 mt-1">
            {isAdmin() ? 'Manage and approve offboarding requests' : 'Submit offboarding requests for approval'}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-offboarding-btn">
              <Plus className="w-4 h-4 mr-2" />
              {isAdmin() ? 'Schedule Offboarding' : 'Request Offboarding'}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Schedule Offboarding</DialogTitle>
              <DialogDescription>
                Schedule calendar deletion and access revocation for a CRM user
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Select
                    value={selectedLocation}
                    onValueChange={setSelectedLocation}
                  >
                    <SelectTrigger data-testid="offboarding-location-select">
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map((loc) => (
                        <SelectItem key={loc.id} value={loc.id}>
                          {loc.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedLocation && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>CRM User *</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={syncCrmUsers}
                        disabled={syncingUsers}
                        className="h-7 text-xs"
                      >
                        {syncingUsers ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3 h-3 mr-1" />
                        )}
                        Sync Users
                      </Button>
                    </div>
                    <Select
                      value={formData.user_id}
                      onValueChange={(val) => setFormData({ ...formData, user_id: val })}
                    >
                      <SelectTrigger data-testid="offboarding-user-select">
                        <SelectValue placeholder="Select CRM user" />
                      </SelectTrigger>
                      <SelectContent>
                        {crmUsers.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            <div className="flex items-center gap-2">
                              <span>{user.name}</span>
                              {user.calendar_ids?.length > 0 && (
                                <Badge variant="outline" className="text-xs">
                                  {user.calendar_ids.length} cal
                                </Badge>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {crmUsers.length === 0 && (
                      <p className="text-xs text-slate-500">
                        No users found. Click &quot;Sync Users&quot; to fetch from CRM.
                      </p>
                    )}
                  </div>
                )}
                
                {formData.user_id && (
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm font-medium text-slate-700">
                      {getSelectedUser()?.name}
                    </p>
                    <p className="text-xs text-slate-500">{getSelectedUser()?.email}</p>
                    {getSelectedUser()?.calendar_ids?.length > 0 && (
                      <p className="text-xs text-amber-600 mt-1">
                        {getSelectedUser()?.calendar_ids?.length} calendar(s) will be deleted
                      </p>
                    )}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label>Reason for Offboarding *</Label>
                  <Textarea
                    placeholder="e.g., Employee resignation, role change, contract ended..."
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    data-testid="offboarding-reason"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Offboarding Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="offboarding-date-btn"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {date ? format(date, 'PPP') : <span className="text-muted-foreground">Pick a date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-3 pt-2">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="delete_calendar"
                      checked={formData.delete_calendar}
                      onCheckedChange={(checked) => setFormData({ ...formData, delete_calendar: checked })}
                      data-testid="delete-calendar-checkbox"
                    />
                    <label htmlFor="delete_calendar" className="text-sm font-medium cursor-pointer">
                      Delete user&apos;s calendars from CRM
                    </label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="revoke_access"
                      checked={formData.revoke_access}
                      onCheckedChange={(checked) => setFormData({ ...formData, revoke_access: checked })}
                      data-testid="revoke-access-checkbox"
                    />
                    <label htmlFor="revoke_access" className="text-sm font-medium cursor-pointer">
                      Remove user from CRM location
                    </label>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    placeholder="Add any notes about this offboarding..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    data-testid="offboarding-notes"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  disabled={submitting || !formData.user_id || !formData.location_id || !date || !formData.reason}
                  data-testid="submit-offboarding-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Schedule'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tasks Table */}
      {tasks.length === 0 ? (
        <Card className="border-dashed border-2 border-slate-200">
          <CardContent className="py-12 text-center">
            <UserMinus className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              No offboarding tasks
            </h3>
            <p className="text-slate-500 mb-6">
              Schedule offboarding when CRM users leave to clean up their calendars and access
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>User</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Offboarding Date</TableHead>
                  <TableHead>Created Date</TableHead>
                  <TableHead>Requested By</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => (
                  <TableRow key={task.id} className="table-row" data-testid={`task-row-${task.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{task.user_name}</p>
                        
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-slate-700">{task.location_name}</p>
                    </TableCell>
                    <TableCell>
                      <p className="text-slate-700 font-medium">
                        {new Date(task.offboarding_date).toLocaleDateString()}
                      </p>
                    </TableCell>
                    <TableCell>
                      <p className="text-slate-500 text-sm">
                        {task.created_at ? new Date(task.created_at).toLocaleDateString() : '-'}
                      </p>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium text-slate-800">{task.requested_by_name || '-'}</p>
                        {task.requested_by_email && (
                          <p className="text-xs text-slate-500">{task.requested_by_email}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <p className="text-slate-600 text-sm max-w-[200px] truncate" title={task.reason || task.notes || '-'}>
                        {task.reason || task.notes || '-'}
                      </p>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(task.status)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Approve/Reject for pending requests - Admin only */}
                        {isAdmin() && task.status === 'pending_approval' && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              onClick={() => handleApprove(task.id)}
                              data-testid={`approve-task-${task.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => handleReject(task.id)}
                              data-testid={`reject-task-${task.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Reject
                            </Button>
                          </>
                        )}
                        {/* Execute for scheduled tasks - Admin only */}
                        {isAdmin() && task.status === 'scheduled' && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            onClick={() => {
                              setSelectedTask(task);
                              setExecuteDialogOpen(true);
                            }}
                            data-testid={`execute-task-${task.id}`}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Execute
                          </Button>
                        )}
                        {/* Delete - Admin only */}
                        {isAdmin() && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            onClick={() => {
                              setSelectedTask(task);
                              setDeleteDialogOpen(true);
                            }}
                            data-testid={`delete-task-${task.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Execute Confirmation */}
      <AlertDialog open={executeDialogOpen} onOpenChange={setExecuteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Execute Offboarding
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>This will immediately perform the following actions in your CRM:</p>
              <ul className="list-disc list-inside text-sm space-y-1 mt-2">
                {selectedTask?.delete_calendar && (
                  <li>Delete all calendars linked to this user from CRM</li>
                )}
                {selectedTask?.revoke_access && (
                  <li>Remove the user from the CRM location</li>
                )}
              </ul>
              <p className="pt-2 font-medium text-slate-900">This action cannot be undone.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleExecute}
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-execute-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Executing...
                </>
              ) : (
                'Execute Now'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this offboarding task? This will not undo any actions that have already been executed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="confirm-delete-task-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OffboardingPage;
